import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FieldCard, FormField, Skeleton } from '@components';
import { getPageRulesFormConfig } from '@constants';
import { Dictionaries, FormType, StatusType, Translation, pageRulesRedirectOptionType } from '@enums';
import {
  useAppDispatch,
  useFormContent,
  useFormError,
  useFormLoadingStatus,
  useFormSaveStatus,
  useFormSubmissionStatus,
} from '@hooks';
import { addSnack, submitFormRequest } from '@store';
import type { FormFieldProps, PageRulesFormProps } from '@types';
import { getOptionListFromCatalog } from '@utils';

interface PredefinedPageRulesViewProps {
  onFormikInstance: any;
}

export const PredefinedPageRulesForm = ({ onFormikInstance }: PredefinedPageRulesViewProps) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnPageRules;

  const { t: tToasts } = useTranslation(Translation.Configuration, { keyPrefix: 'toasts.predefinedPageRules' });
  const { t: tForms } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.pageRules' });

  const [showCurrentToast, setShowCurrentToast] = useState<boolean>(false);
  const [currentDataKey, setCurrentDataKey] = useState<any>(null);

  const pageRulesList = useFormContent({ formType });
  const isDataLoading = useFormLoadingStatus({ formType });
  const isSubmitting = useFormSubmissionStatus({ formType });
  const submitError = useFormError({ formType });
  const saveSuccess = useFormSaveStatus(formType);

  const initialPageRulesValues = {
    http_redirection: pageRulesRedirectOptionType.No_Redirection,
  };

  const initialValues = {
    http_redirection: pageRulesList?.http_redirection ?? initialPageRulesValues.http_redirection,
  };

  const handleSubmit = (values: PageRulesFormProps) => {
    dispatch(
      submitFormRequest({
        formType,
        formData: { application_id, ...values },
        globalOptions: {
          returnResult: true,
        },
      }),
    );
  };

  useEffect(() => {
    if (!submitError && !isSubmitting && saveSuccess) {
      if (showCurrentToast && currentDataKey) {
        dispatch(addSnack({ type: StatusType.Success, message: tToasts(`${currentDataKey}Configured`) }));
        setShowCurrentToast(false);
        setCurrentDataKey(null);
      }
    }
  }, [application_id, currentDataKey, isSubmitting, saveSuccess, showCurrentToast, submitError]);

  const httpRedirectionOption = useMemo(
    () => getOptionListFromCatalog({ name: Dictionaries.PageRulesRedirectOption }),
    [tForms],
  );

  const renderFormField = (field: FormFieldProps, formikValues: PageRulesFormProps) => {
    const { name, label, description, showChip, selectedInput, ...props } = field;

    return (
      <FieldCard key={name} title={label} description={description} showChip={showChip} selectedInput={selectedInput}>
        <FormField name={name} label={label} key={name} {...props} />
      </FieldCard>
    );
  };

  const pageRulesFields = useMemo(
    () =>
      getPageRulesFormConfig({
        options: httpRedirectionOption,
        loading: isSubmitting,
      })?.inputList,
    [httpRedirectionOption, isSubmitting],
  );

  if (isDataLoading)
    return (
      <div className="space-y-4">
        {[...Array(1)].map((_, index) => (
          <Skeleton key={index} className="flex w-full rounded-2xl h-32" />
        ))}
      </div>
    );

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        if (onFormikInstance) onFormikInstance(formik);
        return pageRulesFields.map((field, index) => renderFormField(field, formik.values));
      }}
    </Formik>
  );
};
